import React, { useState, useEffect } from "react";
import {
  Card,
  Collapse,
  Typography,
  Tag,
  Steps,
  Button,
  Row,
  Col,
  Badge,
  Divider,
  Spin,
} from "antd";
import {
  DownOutlined,
  RightOutlined,
  ProjectOutlined,
  CheckCircleOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import { ENDPOINT } from "../../Services/ENDPOINT";
import { ProjectTaskService } from "../../Services/Manage/ProjectTaskService";
import TaskService from "../../Services/Manage/TaskService";
import { UserService } from "../../Services/Manage/UserService";


const getPermission = (link) => {
  try {
    const raw = localStorage.getItem("ome_permission");
    const parsed = JSON.parse(raw);
    const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
    return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false };
  } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const { Text, Title } = Typography;

const ProjectCard = () => {

  const perm = getPermission('/project-timeline');
  const viewAll = perm.view_all === true;
  const [sortOrderMap, setSortOrderMap] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [userMap, setUserMap] = useState({});

  const [projects, setProjects] = useState([]);
  const [tasksMap, setTasksMap] = useState({});
  const [taskHistoryMap, setTaskHistoryMap] = useState({});
  const [loading, setLoading] = useState(true);
  const companyName = "Project TimeLine";

  useEffect(() => {
    (async () => {
      try {
        const user = await UserService.getOmeUser();
        setCurrentUser(user);
      } catch (error) {
        console.error(error);
      }
    })();

    (async () => {
      try {
        const res = await UserService.getAllUsers();
        const list = res?.data || res || [];

        const map = {};
        list.forEach((u) => { map[u.user_id] = u.name; });
        setUserMap(map);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    })();
  }, []);

  const priorityColors = { High: "red", Medium: "orange", Low: "green" };
  const statusColors = {
    Created: "gray",
    "In Progress": "blue",
    "On Hold": "orange",
    Completed: "green",
    Live: "green",
    done: "green",
    complete: "blue",
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(ENDPOINT.Project);
      let data = response.data.data || [];

      if (!viewAll) {
        const uid = String(currentUser?.uid ?? "").trim();
        data = data.filter((p) => {
          let engineers = p.pro_engineer;
          if (typeof engineers === "string") {
            try { engineers = JSON.parse(engineers); } catch { engineers = []; }
          }
          const isEngineer = (engineers || []).some((id) => String(id).trim() === uid);
          return (
            String(p.pro_manager ?? "").trim() === uid ||
            String(p.added_by ?? "").trim() === uid ||
            isEngineer
          );
        });
      }

      const formattedProjects = data.map((item, index) => ({
        id: item.id,
        name: item.pro_name || `Project ${index + 1}`,
        createdOn: item.added_on
          ? moment(item.added_on).format("YYYY-MM-DD")
          : "-",
        status: item.pro_status || "Pending",
        managerId: item.pro_manager || null,
        createdBy: item.added_by || null,
      }));
      setProjects(formattedProjects);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async (projectId) => {
    try {
      const response = await ProjectTaskService.getAllByProject(projectId);

      if (response.status) {
        let tasks = response.data || [];

        if (!viewAll) {
          const uid = String(currentUser?.uid ?? "").trim();
          const uname = String(currentUser?.name ?? "").trim().toLowerCase();
          const proj = projects.find((p) => p.id === projectId);
          const isManagerOfThisProject = proj && String(proj.managerId ?? "").trim() === uid;

          if (!isManagerOfThisProject) {
            tasks = tasks.filter((t) => {
              const addedByMatch = String(t.added_by ?? "").trim() === uid;
              const assigneeMatch = (t.assignee || "").trim().toLowerCase() === uname;
              return addedByMatch || assigneeMatch;
            });
          }
        }

        setTasksMap((prev) => ({ ...prev, [projectId]: tasks }));
        tasks.forEach((task) => fetchTaskHistory(task.id));
      } else {
        setTasksMap((prev) => ({ ...prev, [projectId]: [] }));
      }

    } catch (error) {
      console.error("Error fetching tasks:", error);
      setTasksMap((prev) => ({ ...prev, [projectId]: [] }));
    }
  };


  const fetchTaskHistory = async (taskId) => {
    try {
      const response = await TaskService.getTimeLine(taskId);

      if (response?.status) {
        setTaskHistoryMap((prev) => ({ ...prev, [taskId]: response.data || [] }));
      } else {
        setTaskHistoryMap((prev) => ({ ...prev, [taskId]: [] }));
      }
    } catch (error) {
      console.error("Error fetching task history:", error);
      setTaskHistoryMap((prev) => ({ ...prev, [taskId]: [] }));
    }
  };

  useEffect(() => {
    if (!viewAll && !currentUser) return;
    fetchProjects();
  }, [currentUser]);

  useEffect(() => {
    projects.forEach((project) => {
      fetchTasks(project.id);
    });
  }, [projects]);

  const expandedRowRender = (projectId) => {
    const tasks = tasksMap[projectId] || [];
    if (!tasks.length)
      return <Text type="secondary">No tasks found for this project.</Text>;

    return (
      <Collapse
        accordion
        expandIcon={({ isActive }) =>
          isActive ? <DownOutlined /> : <RightOutlined />
        }
        expandIconPosition="start"
      >
        {tasks.map((task) => {
          const latestStatus = taskHistoryMap[task.id]?.length
            ? taskHistoryMap[task.id][taskHistoryMap[task.id].length - 1].status
            : "Created";

          return (
            <Collapse.Panel
              key={task.id}
              header={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Text strong>{task.task_name}</Text>
                  <Tag
                    color={statusColors[latestStatus]}
                    style={{ fontWeight: "bold" }}
                  >
                    {latestStatus}
                  </Tag>
                </div>
              }
            >
              <Card size="small" style={{ padding: "12px 16px", marginBottom: 12 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                  }}
                >
                  <Text>
                    <strong>Task Name:</strong> {task.task_name}
                  </Text>
                  <Text>
                    <strong>Description:</strong> {task.description}
                  </Text>
                  <Text>
                    <strong>Priority:</strong>{" "}
                    <Tag
                      color={priorityColors[task.priority]}
                      style={{ fontWeight: "bold" }}
                    >
                      {task.priority}
                    </Tag>
                  </Text>
                  <Text>
                    <strong>Created By:</strong> {userMap[task.added_by] || task.added_by || "-"}
                  </Text>
                </div>
              </Card>

              <Card size="small" style={{ marginTop: 12 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <Title level={5} style={{ margin: 0 }}>
                    History
                  </Title>
                  <Button
                    type="link"
                    onClick={() =>
                      setSortOrderMap((prev) => ({
                        ...prev,
                        [task.id]: prev[task.id] === "asc" ? "desc" : "asc",
                      }))
                    }
                    style={{ padding: 0 }}
                  >
                    <span style={{ color: "#000", fontWeight: "bold" }}>Sort: </span>
                    {(sortOrderMap[task.id] || "asc") === "asc"
                      ? "Oldest → Newest"
                      : "Newest → Oldest"}
                  </Button>
                </div>
                {(taskHistoryMap[task.id] || []).length === 0 ? (
                  <Text type="secondary">No history found for this task.</Text>
                ) : (
                  (() => {
                    const historyItems = [...(taskHistoryMap[task.id] || [])];
                    const order = sortOrderMap[task.id] || "asc";

                    const sortedHistory =
                      order === "asc"
                        ? historyItems.sort(
                          (a, b) => new Date(a.added_on) - new Date(b.added_on)
                        )
                        : historyItems.sort(
                          (a, b) => new Date(b.added_on) - new Date(a.added_on)
                        );

                    return (
                      <Steps
                        direction="vertical"
                        size="small"
                        current={-1}
                        items={sortedHistory.map((historyItem) => ({
                          title: historyItem.status,
                          description: (
                            <>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {historyItem.added_on
                                  ? moment.utc(historyItem.added_on).local().format("YYYY-MM-DD HH:mm:ss")
                                  : "-"}
                              </Text>
                              <div>{historyItem.comment}</div>
                              <div style={{ fontStyle: "italic", fontSize: 12 }}>
                                {historyItem.note}
                                {historyItem.added_by ? ` — by ${userMap[historyItem.added_by] || historyItem.added_by}` : ""}
                              </div>
                            </>
                          ),
                        }))}
                      />
                    );
                  })()
                )}

              </Card>

            </Collapse.Panel>
          );
        })}
      </Collapse>
    );
  };

  const handleProjectExpand = (projectId) => {
    if (!tasksMap[projectId]) fetchTasks(projectId);
  };

  const totalProjects = projects.length;
  const liveProjects = projects.filter((p) => p.status === "Live").length;
  const totalTasks = Object.values(tasksMap).reduce(
    (sum, tasks) => sum + tasks.length,
    0
  );

  if (!perm.read) {
    return (
      <Card title={<Title level={4} style={{ margin: 0, color: "#1658b4" }}>{companyName}</Title>} style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Text type="secondary">You don't have permission to view this page.</Text>
      </Card>
    );
  }

  return (
    <Card
      title={<Title level={4} style={{ margin: 0, color: "#1658b4" }}>{companyName}</Title>}
      style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Row gutter={[24, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card
                size="small"
                hoverable
                style={{
                  textAlign: "center",
                  borderRadius: 8,
                  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)",
                  transition: "transform 0.2s",
                }}
              >
                <AppstoreOutlined
                  style={{
                    fontSize: 28,
                    color: "#722ed1",
                    marginBottom: 8,
                  }}
                />
                <div>
                  <Text strong style={{ fontSize: 20 }}>
                    {totalProjects}
                  </Text>
                </div>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  Total Projects
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={8}>
              <Card
                size="small"
                hoverable
                style={{
                  textAlign: "center",
                  borderRadius: 8,
                  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)",
                  transition: "transform 0.2s",
                }}
              >
                <ProjectOutlined
                  style={{
                    fontSize: 28,
                    color: "#1890ff",
                    marginBottom: 8,
                  }}
                />
                <div>
                  <Text strong style={{ fontSize: 20 }}>
                    {liveProjects}
                  </Text>
                </div>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  Live Projects
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={8}>
              <Card
                size="small"
                hoverable
                style={{
                  textAlign: "center",
                  borderRadius: 8,
                  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)",
                  transition: "transform 0.2s",
                }}
              >
                <CheckCircleOutlined
                  style={{
                    fontSize: 28,
                    color: "#52c41a",
                    marginBottom: 8,
                  }}
                />
                <div>
                  <Text strong style={{ fontSize: 20 }}>
                    {totalTasks}
                  </Text>
                </div>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  Total Tasks
                </Text>
              </Card>
            </Col>
          </Row>


          {/* <Title level={5} style={{ margin: "9px 0" }}>
            Project Name
          </Title> */}
          <Divider style={{ margin: "8px 0" }} />

          <Steps
            direction="vertical"
            progressDot
            current={-1}
            items={projects.map((project) => ({
              title: (
                <div style={{ width: "100%", marginBottom: 8 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 4,
                    }}
                  >
                    <Text strong style={{ fontSize: 15 }}>{project.name}</Text>
                    <Tag
                      color={project.status === "Live" ? "green" : "red"}
                      style={{ fontWeight: "bold", marginLeft: 8 }}
                    >
                      {project.status}
                    </Tag>
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Created on: {project.createdOn}
                    {project.createdBy ? ` | Created by: ${userMap[project.createdBy] || project.createdBy}` : ""}
                  </Text>
                </div>
              ),
              description: (
                <Collapse
                  items={[
                    {
                      key: project.key,
                      label: (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            fontWeight: 600,
                            color: "#1890ff",
                            fontSize: 14,
                            cursor: "pointer",
                          }}
                          onClick={() => handleProjectExpand(project.id)}
                        >
                          <span>View Tasks</span>
                          <Badge
                            count={tasksMap[project.id]?.length || 0}
                            style={{
                              backgroundColor: "#1890ff",
                              boxShadow: "0 0 0 1px #fff",
                            }}
                          />
                        </div>
                      ),
                      children: expandedRowRender(project.id),
                    },
                  ]}
                  expandIcon={({ isActive }) =>
                    isActive ? <DownOutlined /> : <RightOutlined />
                  }
                  expandIconPosition="start"
                />
              ),
            }))}
          />
        </>
      )}
    </Card>
  );
};

export default ProjectCard;

